import React from 'react';
import env from '../../../env';
import { usePost } from '../../../components';
import { useForm, validators } from '../../../form';
import { Button, ErrorMessage } from '../../../components';
import { getContactModel } from './contactFormModel';
import { gtm } from '../../../lib/tracking';

export function Contact(props) {

  const emailReceiver = props.emailReceiver;
  const emailReceiverACT = props.emailReceiverACT;
  const emailReceiverNSW = props.emailReceiverNSW;
  const emailReceiverNT = props.emailReceiverNT;
  const emailReceiverQLD = props.emailReceiverQLD;
  const emailReceiverSA = props.emailReceiverSA;
  const emailReceiverTAS = props.emailReceiverTAS;
  const emailReceiverVIC = props.emailReceiverVIC;
  const emailReceiverWA = props.emailReceiverWA;
  const emailReceiverInternational = props.emailReceiverInternational;
  const fileAttachment = props.fileAttachment || 'false';
  const mailchimpApiKey = props.mailchimpApiKey;
  const mailchimpAudienceKey = props.mailchimpAudienceKey;
  
  const model = getContactModel({});
  const form = useForm(model, { 
    usePlaceholder: true,
    onControlChanged: (e, control) => {
      // Handle country change for dynamic validation
      if (control.name === 'country') {
        handleCountryChange(control.value);
      }
    }
  });
  const [sendingStatus, setSendingStatus] = React.useState(null);
  const [mailChimpError, setMailChimpError] = React.useState();
  const [formKey, setFormKey] = React.useState(0); // Key to force form re-render on reset
  const post = usePost();

  const mailChimpErrorMemo = React.useMemo(() => {
    return mailChimpError
  }, [mailChimpError]);



  // Handle country change to update validation
  const handleCountryChange = (country) => {
    const stateControl = form.findControl('state');
    const suburbControl = form.findControl('suburb');
    const internationalCountryControl = form.findControl('internationalCountry');
    
    if (country === 'International') {
      // State field is always required, but set to INTERNATIONAL for routing purposes
      stateControl.value = 'INTERNATIONAL';
      suburbControl.value = '';
      
      // Make suburb optional for International (it's hidden)
      suburbControl.validators = suburbControl.validators.filter(v => v.type !== 'required');
      
      // Make international country field required
      if (!internationalCountryControl.validators.some(v => v.type === 'required')) {
        internationalCountryControl.validators.push(validators.Required());
      }
      internationalCountryControl.attrs = { ...internationalCountryControl.attrs, disabled: false };
      
    } else if (country === 'Australia') {
      // State field is always required, clear value when switching to Australia
      stateControl.value = '';
      
      // Make suburb required for Australia
      if (!suburbControl.validators.some(v => v.type === 'required')) {
        suburbControl.validators.push(validators.Required());
      }
      
      // Make international country field optional (it's hidden)
      internationalCountryControl.validators = internationalCountryControl.validators.filter(v => v.type !== 'required');
      internationalCountryControl.attrs = { ...internationalCountryControl.attrs, disabled: true };
      internationalCountryControl.value = '';
    }
  };

  function onSubmit(e) {
    setMailChimpError(null);
    
    const formValues = form.getValue();
    
    form.validateForm(e, () => {
      
      // Check if reCAPTCHA is available and configured
      if (typeof window.grecaptcha === 'undefined' || !env.reCaptchaSiteKey) {
        
        const value = {
          ...formValues,
          emailReceiver: emailReceiver, // Send default email receiver
          emailReceiverACT: emailReceiverACT,
          emailReceiverNSW: emailReceiverNSW,
          emailReceiverNT: emailReceiverNT,
          emailReceiverQLD: emailReceiverQLD,
          emailReceiverSA: emailReceiverSA,
          emailReceiverTAS: emailReceiverTAS,
          emailReceiverVIC: emailReceiverVIC,
          emailReceiverWA: emailReceiverWA,
          emailReceiverInternational: emailReceiverInternational,
          mailchimpApiKey: mailchimpApiKey,
          mailchimpAudienceKey: mailchimpAudienceKey,
          grecaptchaToken : null // Null token when reCAPTCHA not available
        };
        
        var files = form.getFiles();
        var formData = new FormData();
        formData.append('jsonData', JSON.stringify(value))
        files.forEach(file => {
          (file.files || []).forEach(uploadFile => {
            formData.append(file.name, uploadFile)
          })
        });
        
        post.sendFormData(env.apiBase + "/api/contact/send", formData);
        setSendingStatus('pending');
        return;
      }
      
      window.grecaptcha.ready(function () {
        window.grecaptcha.execute(env.reCaptchaSiteKey, { action: 'submit' }).then(function (token) {

          const value = {
            ...formValues,
            emailReceiver: emailReceiver, // Send default email receiver
            emailReceiverACT: emailReceiverACT,
            emailReceiverNSW: emailReceiverNSW,
            emailReceiverNT: emailReceiverNT,
            emailReceiverQLD: emailReceiverQLD,
            emailReceiverSA: emailReceiverSA,
            emailReceiverTAS: emailReceiverTAS,
            emailReceiverVIC: emailReceiverVIC,
            emailReceiverWA: emailReceiverWA,
            emailReceiverInternational: emailReceiverInternational,
            mailchimpApiKey: mailchimpApiKey,
            mailchimpAudienceKey: mailchimpAudienceKey,
            grecaptchaToken : token
          };
          
          var files = form.getFiles();
          var formData = new FormData();
          formData.append('jsonData', JSON.stringify(value))
          files.forEach(file => {
            (file.files || []).forEach(uploadFile => {
              formData.append(file.name, uploadFile)
            })
          });
          
          post.sendFormData(env.apiBase + "/api/contact/send", formData);
          setSendingStatus('pending');

        }).catch(function(error) {
          console.error('reCAPTCHA error:', error);
        });
      });
    }, (errors) => {
      // Form validation errors are handled by the ErrorMessage component
    })
  }

  if (post.done() && sendingStatus === 'pending') {
    var value = form.getValue();
    gtm.sendEvent('contactFormSubmitted', {contactEmail: value.email} )
    
    // Create a fresh model with empty values for reset
    const freshModel = getContactModel({});
    
    // Reset form to default state with fresh model
    form.reset(freshModel);
    
    // Increment form key to force complete re-render of form (fixes select field reset)
    setFormKey(prevKey => prevKey + 1);
    
    setSendingStatus(null);

    if(post.response.results && (post.response.results.MailChimpError && post.response.results.MailChimpError !== "")){
      setMailChimpError("Mail Chimp Error : " + post.response.results.MailChimpError);
    }
  }

  const render = (name) => form.renderControl(name, null);

  return (
    <form className="contactForm" key={formKey}>
      <div className="form-row">
        <div className="col-md-4 mb-2">{render('name')}</div>
        <div className="col-md-4 mb-2">{render('phone')}</div>
        <div className="col-md-4 mb-2">{render('email')}</div>
      </div>
      <div className="form-row">
        <div className="col-md-4 mb-2">{render('country')}</div>
        {form.findControl('country').value === 'Australia' && (
          <>
            <div className="col-md-4 mb-2">{render('state')}</div>
            <div className="col-md-4 mb-2">{render('suburb')}</div>
          </>
        )}
        {form.findControl('country').value === 'International' && (
          <div className="col-md-4 mb-2">{render('internationalCountry')}</div>
        )}
      </div>
      <div className="form-row">
        <div className="col-md-6 mb-2">{render('organization')}</div>
        <div className="col-md-6 mb-2">{render('service')}</div>
      </div>
      <div className="form-row">
        <div className="col-md-12 mb-2">{render('enquiry')}</div>
      </div>

      { fileAttachment === 'true' &&
        <div className="form-row">
          <div className="col-md-12 mb-2"> {render('attachments')} </div>
        </div>
      }

      <div className="form-row">
        <div className="col-md-12 mb-2">
          <div className="actions">
            <Button onClick={onSubmit} status={post.status}>Send Message</Button>
            <ErrorMessage errors={form.errors} summaryMessage="Please review the errors." />
            <ErrorMessage errors={post.errors} />
            <ErrorMessage errors={mailChimpErrorMemo} />
            {post.done() && <span style={{marginLeft: '1.5rem', color:'red', fontWeight: 'bold'}}>Successfully sent!</span>}
          </div>
        </div>
      </div>
    </form>
  );
}